import React, { useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { LosLogo } from "@/components/Logo";

const HERO = "/brand/scana-skarpenord.jpg";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  // E-mail may be prefilled from the environment; the password is always typed – it never lives in source, env or build.
  const [email, setEmail] = useState(process.env.REACT_APP_LOGIN_PREFILL_EMAIL || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  // embed=1: the proposal page (/tilbud) shows the workspace inside a frame and always starts on sign-in
  const embed = params.get("embed") === "1";
  const home = loc.state?.from?.pathname || "/";

  if (!loading && user && !embed) return <Navigate to={home} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError("Fyll inn e-post og passord"); return; }
    setBusy(true); setError(null);
    try {
      await login(email.trim(), password);
      navigate(home, { replace: true });
    } catch (err) {
      const msg = errMsg(err);
      setError(msg);
      if (err?.response?.status === 429) toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background lg:grid-cols-[1.15fr_1fr]" data-testid="login-page" data-embed={embed ? "1" : "0"}>
      <section className="relative hidden overflow-hidden bg-[hsl(var(--brand))] lg:block" aria-hidden="true">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full select-none object-cover object-center fade-in" style={{ filter: "saturate(0.95) contrast(1.02)" }} draggable={false} data-testid="login-brand-image" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,hsl(210_100%_12%/0.5)_0%,hsl(210_100%_12%/0.12)_30%,hsl(210_100%_12%/0.1)_100%)]" />
        <div className="absolute left-12 top-10 xl:left-16"><LosLogo variant="white" className="text-[40px]" testId="login-logo" /></div>
      </section>

      <section className="flex flex-col px-6 py-8 sm:px-12 lg:px-16 xl:px-24">
        <div className="lg:hidden"><LosLogo className="text-[18px]" testId="login-logo-mobile" /></div>
        <div className="flex flex-1 items-center justify-center">
          <form onSubmit={submit} className="w-full max-w-[380px]" data-testid="login-form" noValidate>
            <div className="fade-up">
              <h1 className="font-display text-[32px] font-extrabold leading-tight tracking-[-0.035em] text-[hsl(var(--navy))]">Logg inn</h1>
              <p className="mt-2 text-[14.5px] text-muted-foreground">Skarpenords arbeidsflate for konfigurasjon, kalkyle og tilbud.</p>
            </div>

            <div className="fade-up d-1 mt-8 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[13px] font-medium">E-post</Label>
                <Input id="email" type="email" autoComplete="username" autoFocus={!embed} placeholder="din e-post" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl border-black/[0.1] bg-white px-3.5 text-[15px] focus-visible:ring-primary/30" data-testid="login-email-input" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[13px] font-medium">Passord</Label>
                <div className="relative">
                  <Input id="password" type={showPw ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-xl border-black/[0.1] bg-white px-3.5 pr-11 text-[15px] focus-visible:ring-primary/30" data-testid="login-password-input" required />
                  <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Skjul passord" : "Vis passord"} data-testid="login-toggle-password-button" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-black/[0.05] hover:text-foreground">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            {error ? <p className="mt-3 rounded-[10px] bg-[hsl(0_50%_46%/0.06)] px-3 py-2 text-[12.5px] text-[hsl(var(--v-blocked))]" role="alert" data-testid="login-error">{error}</p> : null}

            <Button type="submit" size="lg" disabled={busy} className="fade-up d-2 mt-7 h-12 w-full gap-2 rounded-full text-[15px] font-semibold" data-testid="login-submit-button">
              {busy ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> Logger inn</> : <>Logg inn <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
        </div>
        <div className="flex items-center justify-between text-[12px] text-muted-foreground"><span>© {new Date().getFullYear()} Skarpenord AS</span><span>Rjukan</span></div>
      </section>
    </div>
  );
}
